import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import utc from 'dayjs/plugin/utc'
import OpalHeading from '../../../../components/opal/OpalHeading'
import FlexRow from '../../../../components/molecules/FlexRow'
import FlexColumn from '../../../../components/molecules/FlexColumn'
import OpalCard from '../../../../components/opal/OpalCard'
import Icon from '../../../../components/atoms/Icon'
import { ICON_NAMES } from '../../../../constants'
import { useMarkNotificationRead } from '../../../../api/meetings'

dayjs.extend(relativeTime)
dayjs.extend(utc)

const useStyles = makeStyles(() => ({
  card: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--dai-color-hover) !important'
    }
  },
  iconWrapper: {
    width: 32,
    height: 32,
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-primary-050)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: 'var(--color-primary-700)'
  },
  secondaryLink: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-primary-600)',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' }
  }
}))

const PLATFORM_LABELS = {
  teams: 'Teams',
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  webex: 'Webex'
}

function MeetingNotificationItem ({ notification }) {
  const classes = useStyles()
  const history = useHistory()
  const markRead = useMarkNotificationRead()

  const markAsRead = useCallback(() => {
    if (notification.status === 'unread') {
      markRead.mutate({ meetingNotificationId: notification.meetingNotificationId })
    }
  }, [notification, markRead])

  const handleClick = useCallback(() => {
    markAsRead()
    if (notification.threadId) {
      history.push(`/denali-ai/chat/${notification.threadId}`)
    } else {
      history.push(`/denali-ai/meetings/${notification.meetingId}`)
    }
  }, [notification, history, markAsRead])

  const handleSecondaryClick = useCallback((e) => {
    e.stopPropagation()
    markAsRead()
    if (notification.threadId) {
      history.push(`/denali-ai/meetings/${notification.meetingId}`)
    }
  }, [notification, history, markAsRead])

  const timeDisplay = notification.startAt
    ? dayjs.utc(notification.startAt).local().format('MMM D, YYYY h:mm A')
    : dayjs.utc(notification.createdDate).local().fromNow()

  const platformLabel = PLATFORM_LABELS[notification.platform] || null

  return (
    <div onClick={handleClick}>
      <OpalCard
        className={`font-serif p-lg opal-selectable relative ${classes.card}`}
        style={{ backgroundColor: 'var(--dai-color-surface3)' }}
      >
        <FlexRow className='gap-md'>
          <div className={classes.iconWrapper}>
            <Icon name={ICON_NAMES.calendar} customSize={18} />
          </div>
          <FlexColumn className='gap-sm' style={{ flex: 1 }}>
            <OpalHeading className='font-size-md'>
              {notification.title || 'Meeting Completed'}
            </OpalHeading>
            <FlexRow className='gap-sm font-sans font-size-xs' style={{ color: 'var(--dai-color-text-dim)' }}>
              <span>{timeDisplay}</span>
              {platformLabel && (
                <>
                  <span>&middot;</span>
                  <span>{platformLabel}</span>
                </>
              )}
              {notification.threadId && (
                <>
                  <span>&middot;</span>
                  <span className={classes.secondaryLink} onClick={handleSecondaryClick}>
                    Meeting Details
                  </span>
                </>
              )}
            </FlexRow>
          </FlexColumn>
        </FlexRow>
      </OpalCard>
    </div>
  )
}

MeetingNotificationItem.propTypes = {
  notification: PropTypes.shape({
    meetingNotificationId: PropTypes.number.isRequired,
    meetingId: PropTypes.string.isRequired,
    threadId: PropTypes.string,
    title: PropTypes.string,
    platform: PropTypes.string,
    startAt: PropTypes.string,
    createdDate: PropTypes.string,
    status: PropTypes.string
  }).isRequired
}

export default MeetingNotificationItem
